import React from 'react';
import { message, notification, Modal } from 'antd';
import PropTypes from 'prop-types';
import 'moment/locale/zh-cn';
import { isEmpty, isEqual } from 'lodash';
import { loadViewComponent } from 'modules/ModulerHelper';
import Constants from 'constants/Constants';
import { postFile, postRequest, getRequest, downloadRequest } from 'service/HttpCommunicationService';
import SessionService from 'service/SessionService';
import ObjectUtil from 'util/src/ObjectUtil';
import 'common.less';

export default class BaseComponent extends React.Component {
  // 回避页面迁移后，请求返回问题
  _isMounted = false;

  constructor(props, checkLogin = true) {
    super(props);
    this.checkLogin = checkLogin;
    if (checkLogin && isEmpty(SessionService.userName)) {
      this.jumpToLogin();
    }
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  /**
   * 提示显示（notification）
   */
  showNotification(text, type = 'error', placement = 'bottomRight') {
    notification[type]({ message: text, placement });
  }

  /**
   * 提示显示（message）
   */
  showMessage(text, type = 'error') {
    this.removeEvent();
    message.destroy();
    message.config({
      top: 200,
      duration: 2,
      maxCount: 1
    });

    message[type](text, 2, () => { this.removeEvent(); }).then(this.addMsgEventListener());
  }

  removeEvent = () => {
    // 移除提示消息监听
    document.removeEventListener('click', this.clickMsgBoxOuter);
  }

  addMsgEventListener() {
    document.addEventListener('click', this.clickMsgBoxOuter);
  }

  /**
   * POST 请求
   */
  sendPostRequest(url, body, success, failed) {
    if (!this.isRequestValid()) {
      return;
    }
    postRequest(url, body)
      .then(response => {
        this.successResp(response.data, success, failed);
        this.onFinally();
      })
      .catch(error => {
        this.handleRequestError(error, failed);
        this.onFinally();
      });
    // 兼容低版本浏览器，去掉 ES2018 的finally用法
  }

  /**
   * GET 请求
   */
  sendGetRequest(url, success, failed) {
    if (!this.isRequestValid()) {
      return;
    }
    getRequest(url)
      .then(response => {
        this.successResp(response.data, success, failed);
        this.onFinally();
      })
      .catch(error => {
        this.handleRequestError(error, failed);
        this.onFinally();
      });
    // 兼容低版本浏览器，去掉 ES2018 的finally用法
  }

  uploadFileRequest(url, body, success, failed) {
    if (!this.isRequestValid()) {
      return;
    }
    postFile(url, body)
      .then(response => {
        this.successResp(response.data, success, failed);
        this.onFinally();
      })
      .catch(error => {
        this.handleRequestError(error, failed);
        this.onFinally();
      });
    // 兼容低版本浏览器，去掉 ES2018 的finally用法
  }

  // 获取设置项
  getSystemDropdownList = (callback) => {
    this.sendPostRequest('/system/common/getProfileValueByCode', { code: 'FILE_UPLOAD_REPLACE_CHARSET' }, (result) => {
      if (result && result) {
        const specialChars = result.split(',');
        callback(specialChars);
      } else {
        console.error('获取特殊字符失败', result);
        callback(['#', '%']);
      }
    });
  }

  uploadFileCheckFileNameRequest(url, body, otherBody, success, failed) {
    this.getSystemDropdownList((specialChars) => {
      // 检查其他条件
      if (otherBody && otherBody.checkFlag) {
        const file = otherBody.file;

        if (file) {
          const regexPattern = `([${specialChars.join('')}])`;
          console.log(regexPattern);

          const regex = new RegExp(regexPattern);
          console.log(regex);

          const splitResult = file.name.split(regex);

          const containsSpecialChar = specialChars.some(char => splitResult.includes(char));
          if (containsSpecialChar) {
            this.onError(`上传的文件名称不能包含特殊符号【${specialChars.join('】【')}】，请修改文件名称后再上传`, failed);
            return;
          }
        } else {
          this.showMessage('未传入文件，请选择一个文件进行上传', 'warn');
          return;
        }
      }

      this.uploadFileRequest(url, body, success, failed);
    });
  }

  // 检查请求是否合法
  isRequestValid = () => {
    if (this.checkLogin && SessionService.userName != null && SessionService.localUserName != null && SessionService.userName !== SessionService.localUserName) {
      SessionService.clear();
      this.jumpToLogin('不能同时登陆两个账号，请重新登录');
      return false;
    } else {
      return true;
    }
  };

  /**
  * URL方式下载并打开窗口
  */
  downloadUrlFile(url, body, failed) {
    postRequest(url, body).then(
      (res) => {
        if (res.data.code === Constants.RESPONSE_CODE_SUCCESS) {
          const a = document.createElement('a');
          document.body.appendChild(a);
          a.setAttribute('style', 'display: none');
          a.href = '/report/' + res.data.result;
          var splitValue = res.data.result.split('/');
          a.download = splitValue[splitValue.length - 1];
          a.click();
          a.remove(); // remove the element
          this.showMessage('下载成功，请确认', 'success');
        } else {
          // 业务异常
          this.showMessage(res.data.errorMsg, 'error');
        }
      }
    ).catch((err) => {
      console.log(err);
      this.onError('下载文件失败', failed);
    });
  }

  /**
   * 下载并打开窗口
   */
  openDownloadWindow(url, body, failed) {
    const tab = window.open('', '_blank', 'width=800,height=600,menubar=0,toolbar=0');
    tab.document.writeln('下载中,请稍候...');
    downloadRequest(url, body)
      .then(res => {
        const contentType = res.headers['content-type'];
        const fileBlob = new Blob([res.data], { type: contentType });
        // 下载异常时
        if (res.status !== 200 || contentType == null || contentType.indexOf('text') >= 0 || contentType.indexOf('json') >= 0) {
          tab.close();
          const reader = new FileReader();
          // This fires after the blob has been read/loaded.
          reader.addEventListener('loadend', e => {
            console.log(e.srcElement);
            const element = e.srcElement;
            const text = element.result;
            console.log(text);
            if (isEmpty(text)) {
              this.showMessage('下载异常。');
            } else {
              this.showMessage(text);
            }
          });
          reader.readAsText(fileBlob);
        } else {
          const disposition = res.headers['content-disposition'].split(';');
          const matches = /filename=([^;]+)/gi.exec(disposition[1]);
          const fileName = decodeURIComponent(escape(matches[1].trim()));
          const downloadType = disposition[0].trim();
          const fileUrl = window.URL.createObjectURL(fileBlob);

          // PDF 的情况展示文件
          if (contentType.indexOf('pdf') >= 0 && downloadType != null && downloadType.indexOf('inline') >= 0) {
            if (navigator.appVersion.toString().indexOf('.NET') > 0) {
              // for IE browser
              window.navigator.msSaveOrOpenBlob(fileBlob, fileName);
              tab.close();
              this.showMessage('请保存后，查看', 'success');
            } else {
              tab.location.href = fileUrl;
            }
          } else {
            tab.close();
            const a = document.createElement('a');
            document.body.appendChild(a);
            a.setAttribute('style', 'display: none');
            a.href = fileUrl;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(fileUrl);
            a.remove(); // remove the element
            this.showMessage('下载成功，请确认', 'success');
          }
        }
      })
      .catch(err => {
        tab.close();
        console.log(err);
        this.onError('下载文件失败', failed);
      });
  }

  successResp(response, success, failed) {
    // 返回数据异常时
    if (response == null || response.code == null) {
      this.onError('返回数据异常，请重新提交', failed);
    } else {
      switch (response.code) {
        case Constants.RESPONSE_CODE_SUCCESS:
          if (!ObjectUtil.isEmpty(success) && this._isMounted) {
            success(response.result);
          }
          break;
        case Constants.RESPONSE_CODE_SKIP:
          break;
        default:
          this.onError(response.errorMsg, failed);
          break;
      }
    }
  }

  handleRequestError(error, failed) {
    if (error.response == null) {
      this.onError(error.message, failed);
    } else {
      const response = error.response;
      switch (response.status) {
        // UNAUTHORIZED
        case 401:
          this.jumpToLogin(response.data);
          break;
        // FORBIDDEN
        case 403:
          this.onError(response.data, failed);
          break;
        default:
          this.onError('服务器异常。(' + response.status + ')' + (response.data.errorMsg ? response.data.errorMsg : response.data), failed);
          break;
      }
    }
  }

  /**
   * 错误消息
   */
  onError(errorMessage, failed) {
    if (failed != null) {
      failed(errorMessage);
    } else {
      this.showMessage(errorMessage);
    }
  }

  /**
   * 收尾处理
   */
  onFinally() { }

  /**
   * 调整到登录页面
   */
  jumpToLogin(errorMessage) {
    if (!isEmpty(errorMessage)) {
      this.showMessage(errorMessage);
    }
    this.props.history.push('/login');
  }

  // 操作之前的确认弹框
  // messag: 弹框中部展示文本，  handleOk： 确认按钮点击事件， handleCancel： 取消按钮点击事件
  // 使用示例： DepartmentManagement.js
  confirm(messag, handleOk, handleCancel, content) {
    Modal.confirm({
      title: messag,
      content: content,
      onOk: handleOk,
      onCancel: handleCancel,
      okButtonProps: { id: 'confirmOk' },
      cancelButtonProps: { id: 'confirmCancel' }
    });
  }

  /**
   * 添加Tab
   * title: 标题, content: 显示的组件, key: 唯一标识, closable: 是否可以关闭, props: 传递给组件的参数
   */
  addTab(title, content, key, closable = true, props) {
    if (content === null) {
      this.showMessage('菜单URL配置异常');
      return;
    }
    this.props.addTabPane({ title, content, key, closable, props });
  }

  /**
   * 仅添加Tab,不做跳转(当标签已存在时做跳转)
   * title: 标题, content: 显示的组件, key: 唯一标识, closable: 是否可以关闭, props: 传递给组件的参数, notJumpFlag: 是否
   */
  addTabNotJump(title, content, key, closable = true, props) {
    const notJumpFlag = true;
    this.props.addTabPane({ title, content, key, closable, props, notJumpFlag });
  }

  /**
   * 根据URL添加Tab页
   */
  addTabByUrl(title, url, key, closable, parameter) {
    if (isEmpty(url)) {
      this.showMessage('菜单URL未配置，请联系管理员');
      return;
    }
    // 解析参数
    var props = null;
    props = JSON.parse(parameter);

    props = props || {};
    props.url = url;
    props.parameterBack = parameter;
    props.title = title;

    this.addTab(title, loadViewComponent(url), key, closable, props || {});
  }

  /**
   * 根据PATH添加Tab页
   */
  addTabByPath(title, path, key, props, closable) {
    this.addTab(title, loadViewComponent(path), key, closable, props || {});
  }

  onlyAddTabByPath(title, path, key, props, closable) {
    this.addTabNotJump(title, loadViewComponent(path), key, closable, props || {});
  }

  // 通过鼠标点击位置获取DOM元素
  getDomElementFromMouseEvent(mouseEvent) {
    const x = mouseEvent.pageX || mouseEvent.clientX + document.body.scroolLeft;
    const y = mouseEvent.pageY || mouseEvent.clientY + document.body.scrollTop;
    return document.elementFromPoint(x, y);
  }

  // 检查是否点击表格单选框
  checkClickTableRadio(mouseEvent) {
    if (mouseEvent == null) {
      return false;
    }
    let checkFlag = false;
    const element = this.getDomElementFromMouseEvent(mouseEvent);

    if (element) {
      if (element.className.includes('ant-radio') || element.className.includes('ant-table-selection-column')) {
        checkFlag = true;
      }
    }

    return checkFlag;
  }

  // 键盘操作相关方法
  handleKeyUp = (event, params) => {
    const { inputRef, total, currentPage, pageSize, sortString, onPageChange, addAction } = params;

    // 焦点在检索框时、键盘只能录入信息
    if (inputRef && inputRef.current && inputRef.current.state && inputRef.current.state.focused) {
      return;
    }

    // 获取表格选中radio的焦点
    let checkedRef = null;
    if (document.querySelector('.ant-radio-checked')) {
      checkedRef = document.querySelector('.ant-radio-checked').querySelector('.ant-radio-input');
      checkedRef.focus();

      switch (event.keyCode) {
        // 小键盘【+】、向后翻页
        case Constants.KEYBOARD_NUMBERPAD_ADD:
          if (currentPage < Math.ceil(total / 10)) {
            onPageChange(sortString, currentPage + 1, pageSize);
          }
          break;
        // 小键盘【-】、向前翻页
        case Constants.KEYBOARD_NUMBERPAD_SUBTRACT:
          if (currentPage > 1) {
            onPageChange(sortString, currentPage - 1, pageSize);
          }
          break;
        // 选中明细行后、回车键添加
        case Constants.KEYBOARD_ENTER:
          addAction(Constants.ACTION_KEYBOARD);
          break;
        default:
      }
    }
  };

  // 检查是否鼠标点击
  checkMouseOrNot(event) {
    if (event == null) {
      return false;
    }

    if (event.screenX > 0) {
      return true;
    }
    return false;
  }

  // 查询弹出框获取焦点后、清空选中明细
  handleMouseDown = (event, tableRef, inputId) => {
    const element = this.getDomElementFromMouseEvent(event);
    if (isEqual(element.id, inputId)) {
      tableRef && tableRef.setSelectRows();
    }
  };

  /**
   * 下载文件
   */
  downloadFile(url, body, sucess, failed) {
    downloadRequest(url, body).then(
      (res) => {
        console.log(res);
        if (res.status === 200) {
          const contentType = res.headers['content-type'];
          const fileBlob = new Blob([res.data], { type: contentType });
          const disposition = res.headers['content-disposition'].split(';');
          const matches = /filename=([^;]+)/ig.exec(disposition[1]);
          const fileName = decodeURIComponent(escape(matches[1].trim()));
          const fileUrl = window.URL.createObjectURL(fileBlob);
          console.log(fileUrl);
          const a = document.createElement('a');
          document.body.appendChild(a);
          a.setAttribute('style', 'display: none');
          a.href = fileUrl;
          a.download = fileName;
          a.click();
          window.URL.revokeObjectURL(fileUrl);
          a.remove(); // remove the element
          this.showMessage('下载成功，请确认', 'success');
          sucess();
        }
      }
    ).catch((err) => {
      console.log(err);
      this.onError('下载文件失败', failed);
    });
  }

  refresh = () => {
    this.setState({});
  };
}

BaseComponent.propTypes = {
  history: PropTypes.object.isRequired,
  addTabPane: PropTypes.func.isRequired,
  // 组件 URL，用于标识组件唯一性
  url: PropTypes.string
};
